/**
 * Поповер выбора тега над кнопкой панели выделения: «повесить», «снять» или оба.
 *
 * В режиме `add` у каждого тега — «k/N»: на скольких из выделенных он уже
 * висит; снизу поле «новый тег… ⏎» — завести и сразу повесить. В режиме
 * `remove` — только теги, что есть хотя бы у одного выделенного. Режим `both`
 * (одно меню «Теги» панелей) — список и поле как в `add`, клик по строке вешает,
 * а у строки с k > 0 справа «×» снимает тег с выделенных (`onUnpick`), не вешая.
 *
 * Esc и клик мимо закрывают. Esc ловим сами и не пускаем дальше: иначе тот же
 * Esc снял бы и всё выделение.
 */
import { X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent, type RefObject } from "react";

import { input } from "@/components/trends/ui";
import { tagStyle, type Tag } from "@/lib/tags";

/** Закрыть поповер кликом мимо `box` и по Esc (Esc дальше не идёт). */
export function usePopoverClose(box: RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopImmediatePropagation();
      onClose();
    };
    document.addEventListener("mousedown", onDown);
    // capture — раньше слушателя экрана, который по Esc снимает выделение.
    window.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [box, onClose]);
}

export function TagPicker({
  tags,
  counts,
  total,
  mode,
  onPick,
  onUnpick,
  onCreate,
  onClose,
  busy = false,
}: {
  tags: Tag[];
  /** Тег → на скольких выделенных он уже висит. */
  counts: ReadonlyMap<number, number>;
  total: number;
  mode: "add" | "remove" | "both";
  onPick: (tagId: number) => void;
  /** Только `both`: «×» у висящего тега — снять с выделенных. */
  onUnpick?: (tagId: number) => void;
  onCreate?: (name: string) => void;
  onClose: () => void;
  busy?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState("");

  usePopoverClose(box, onClose);

  const needle = query.trim().toLowerCase();
  // `remove` показывает только висящие; `add` и `both` — весь словарь.
  const adds = mode !== "remove";
  const list = useMemo(
    () =>
      tags.filter(
        (t) => (adds || (counts.get(t.id) ?? 0) > 0) && (!needle || t.name.toLowerCase().includes(needle)),
      ),
    [tags, counts, adds, needle],
  );
  const exact = tags.some((t) => t.name.toLowerCase() === needle);
  const canCreate = adds && onCreate && needle !== "" && !exact;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const name = query.trim().split(/\s+/).join(" ");
    if (canCreate) onCreate(name);
    else if (list.length === 1) onPick(list[0]!.id);
    else {
      const hit = tags.find((t) => t.name.toLowerCase() === needle);
      if (hit && (adds || (counts.get(hit.id) ?? 0) > 0)) onPick(hit.id);
    }
  };

  return (
    <div
      ref={box}
      className="absolute bottom-full left-0 z-10 mb-2 grid w-[260px] gap-1.5 rounded-[var(--radius-card)] border border-border bg-bg p-2 shadow-xl"
      data-annot={mode === "add" ? "повесить тег" : mode === "remove" ? "снять тег" : "теги выделенных"}
    >
      <form onSubmit={submit}>
        <input
          autoFocus
          className={`${input} w-full`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          maxLength={60}
          placeholder={adds ? "найти или новый тег… ⏎" : "найти тег…"}
        />
      </form>
      <div className="max-h-[260px] overflow-auto">
        {list.length === 0 ? (
          <div className="px-1.5 py-2 text-[12px] text-fg-faint">
            {mode === "remove" ? "У выделенных пинов нет тегов." : tags.length ? "Нет совпадений." : "Тегов пока нет."}
          </div>
        ) : (
          list.map((t) => {
            const k = counts.get(t.id) ?? 0;
            const row = (
              <button
                key={t.id}
                type="button"
                disabled={busy}
                onClick={() => onPick(t.id)}
                title={mode === "both" ? "Повесить на все выделенные" : undefined}
                className="flex w-full min-w-0 items-center gap-2 rounded-[var(--radius-ui)] px-1.5 py-1 text-left hover:bg-bg-hover disabled:opacity-50"
              >
                <span className="min-w-0 truncate rounded-[3px] px-1 text-[11.5px]" style={tagStyle(t.id)}>
                  {t.name}
                </span>
                <span className="ml-auto flex-none text-[11px] tnum text-fg-faint" title="Сколько выделенных уже с этим тегом">
                  {k}/{total}
                </span>
              </button>
            );
            if (mode !== "both") return row;
            // «×» — соседняя кнопка, не вложенная: клик по ней строку не вешает.
            return (
              <div key={t.id} className="flex items-center gap-0.5">
                {row}
                {k > 0 && onUnpick ? (
                  <button
                    type="button"
                    disabled={busy}
                    aria-label={`Снять тег ${t.name}`}
                    title="Снять с выделенных"
                    onClick={(e) => {
                      e.stopPropagation();
                      onUnpick(t.id);
                    }}
                    className="grid size-6 flex-none place-items-center rounded-[var(--radius-ui)] text-fg-faint hover:bg-bg-hover hover:text-err disabled:opacity-50"
                  >
                    <X size={12} />
                  </button>
                ) : (
                  <span className="size-6 flex-none" />
                )}
              </div>
            );
          })
        )}
      </div>
      {canCreate ? (
        <button
          type="button"
          disabled={busy}
          onClick={() => onCreate(query.trim().split(/\s+/).join(" "))}
          className="rounded-[var(--radius-ui)] border border-dashed border-border px-1.5 py-1 text-left text-[12px] text-accent-text hover:bg-bg-hover disabled:opacity-50"
        >
          создать «{query.trim()}» и повесить ⏎
        </button>
      ) : null}
    </div>
  );
}
